import { debounce } from 'lodash';
import { mapGetters, mapState, mapActions } from 'vuex';
import appBottomNav from '@/components/app/app-bottom-nav/app-bottom-nav.vue';
import appFooter from '@/components/app/app-footer/app-footer.vue';
import appHeaderNotification from '@/components/app/app-header-notification/app-header-notification.vue';
import appHeader from '@/components/app/app-header/app-header.vue';
import searchBar from '@/components/app/app-header/search-bar/search-bar.vue';
import appMainNav from '@/components/app/app-main-nav/app-main-nav.vue';
import appMobileMenu from '@/components/app/app-mobile-menu/app-mobile-menu.vue';
import appSecondaryNav from '@/components/app/app-secondary-nav/app-secondary-nav.vue';
import navBrowseCategoriesOverlay from '@/components/app/app-secondary-nav/nav-browse-categories-overlay/nav-browse-categories-overlay.vue';
import sideNotification from '@/components/generics/side-notification/side-notification.vue';
import maintenanceNotification from '@/components/maintenance-notification/maintenance-notification.vue';
import notificationItems from '@/components/notification-items/notification-items.vue';
import pinboardPanel from '@/components/pinning/pinboard-panel/pinboard-panel.vue';
import constants from '@/constants';
import env from '@/env';
import { isStandalone } from '@/mixins/userAgent';
import { scrollState } from '@/router';

const SIMPLE_LAYOUT_ROUTES = ['disqus-auth', 'disqus-logout'];

const DEBOUNCE_INTERVAL = 500;
const PINBOARD_SINGLE_MIN_WIDTH = 1459;
const PINBOARD_DUAL_MIN_WIDTH = 1894;

export default {
  components: {
    appHeader,
    appHeaderNotification,
    appSecondaryNav,
    appMainNav,
    appBottomNav,
    appFooter,
    appMobileMenu,
    maintenanceNotification,
    notificationItems,
    searchBar,
    sideNotification,
    navBrowseCategoriesOverlay,
    ...(env.PINNING_ENABLED ? { pinboardPanel } : {}),
  },
  data() {
    return {
      isStandalone,
      isTransitioning: false,
      pinboardObserver: null,
      panelScrollHandler: null,
    };
  },
  created() {
    this.railInners = {};
    this.railObserver = this.isPinningEnabled
      ? new window.ResizeObserver(() => this.updateRailHeight())
      : null;
  },
  watch: {
    $route() {
      setTimeout(() => {
        this.isTransitioning = false;
      }, 1000);
    },
  },
  methods: {
    ...mapActions('app', ['setPinboardModeVisible']),
    onBeforeLeave() {
      this.isTransitioning = true;
    },
    onAfterEnter() {
      this.isTransitioning = false;
      if (scrollState.pendingPosition) {
        const { left, top } = scrollState.pendingPosition;
        scrollState.pendingPosition = null;
        window.scrollTo(left, top);
      } else {
        window.scrollTo(0, 0);
      }
    },
    setRailInnerRef(side, el) {
      const prev = this.railInners[side];
      if (prev === el) return;
      if (prev) this.railObserver.unobserve(prev);
      if (el) {
        this.railInners[side] = el;
        this.railObserver.observe(el);
      } else {
        delete this.railInners[side];
      }
      this.updateRailHeight();
    },
    updateRailHeight() {
      if (!this.$el) return;
      const heights = Object.values(this.railInners).map((el) => el.offsetHeight);
      const height = heights.length ? Math.max(...heights) : 0;
      this.$el.style.setProperty('--rail-height', `${height}px`);
      this.syncRailScroll();
    },
    syncRailScroll() {
      Object.values(this.railInners).forEach((el) => {
        el.parentElement.scrollTop = window.scrollY;
      });
    },
  },
  mounted() {
    if (!this.isPinningEnabled) return;

    const update = debounce(() => {
      const w = window.innerWidth;
      this.setPinboardModeVisible({
        single: w >= PINBOARD_SINGLE_MIN_WIDTH,
        dual: w >= PINBOARD_DUAL_MIN_WIDTH,
      });
    }, DEBOUNCE_INTERVAL);

    this.pinboardObserver = new window.ResizeObserver(update);
    this.pinboardObserver.observe(document.documentElement);
    const w = window.innerWidth;
    this.setPinboardModeVisible({
      single: w >= PINBOARD_SINGLE_MIN_WIDTH,
      dual: w >= PINBOARD_DUAL_MIN_WIDTH,
    });

    const updatePanelBottom = () => {
      const footer = this.$el.querySelector('.app-layout__footer');
      const bottom = footer
        ? Math.max(0, window.innerHeight - footer.getBoundingClientRect().top)
        : 0;
      this.$el.style.setProperty('--panel-bottom', `${bottom}px`);
      this.syncRailScroll();
    };
    this.panelScrollHandler = updatePanelBottom;
    window.addEventListener('scroll', updatePanelBottom, { passive: true });
    updatePanelBottom();
  },
  beforeUnmount() {
    if (this.pinboardObserver) this.pinboardObserver.disconnect();
    if (this.railObserver) this.railObserver.disconnect();
    if (this.panelScrollHandler) {
      window.removeEventListener('scroll', this.panelScrollHandler);
    }
  },
  computed: {
    ...mapGetters('app', [
      'hasBanner',
      'hasMaintenanceMessage',
      'isLayoutDesktop',
      'isPinningEnabled',
      'isPinboardSingleModeVisible',
      'isPinboardDualModeVisible',
      'sidebarVisible',
    ]),
    ...mapState('app', ['hasServerErrorOccurred', 'liveUpdatesDisabled', 'mobileMenuOpened']),
    ...mapState('popup', ['currentPopup', 'currentSmallPopup']),
    ...mapState('notifications', [
      'sideNotificationLevel',
      'sideNotificationText',
      'sideNotificationOnCloseCallback',
      'showNotificationsWidget',
    ]),
    ...mapGetters('app', ['isLayoutMobile']),
    ...mapGetters('user', ['isUserRestricted']),
    ...mapState('search', ['searchBarVisible']),
    isSimpleLayout() {
      return SIMPLE_LAYOUT_ROUTES.includes(this.$route.name);
    },
    appModifiers() {
      return {
        'app-layout': true,
        'app-layout--with-overflow':
          (this.currentPopup !== null || this.currentSmallPopup !== null) && this.isTransitioning,
        'app-layout--full-width': this.$route.meta.appContentFullWidth,
        'app-layout--bg-white': true,
        'app-layout--pinboard': this.isPinboardLayoutEnabled,
        'app-layout--single-left-open': this.showLeftPanel && this.inSinglePanelMode,
        'app-layout--dual-open':
          this.isPinboardDualModeVisible && this.sidebarVisible && this.isPinboardLayoutEnabled,
      };
    },
    headerModifiers() {
      return {
        'app-layout__header': true,
        'app-layout__header--secondary': true,
        'app-layout__header--error':
          this.hasServerErrorOccurred ||
          this.hasMaintenanceMessage ||
          this.hasBanner ||
          this.liveUpdatesDisabled,
        'app-layout__header--notifications-widget-open': this.showNotificationsWidget,
      };
    },
    contentModifiers() {
      return {
        'app-layout__content': true,
        'app-layout__content--secondary': !this.isSimpleLayout,
        'app-layout__content--simple': this.isSimpleLayout,
        'app-layout__content--error':
          this.hasServerErrorOccurred ||
          this.hasMaintenanceMessage ||
          this.hasBanner ||
          this.liveUpdatesDisabled,
        'app-layout__content--with-overflow': this.$route.meta.appContentOverflowed,
        'app-layout__content--full-width': this.$route.meta.appContentFullWidth,
        'app-layout__content--pinboard': this.isPinboardLayoutEnabled,
        'app-layout__content--rail': this.showLeftPanel || this.showRightPanel,
      };
    },
    sideNotificationShouldDisplay() {
      return this.sideNotificationLevel && this.sideNotificationText;
    },
    isPinboardLayoutEnabled() {
      return this.isPinningEnabled && !this.isSimpleLayout;
    },
    inSinglePanelMode() {
      return this.isPinboardSingleModeVisible && !this.isPinboardDualModeVisible;
    },
    showLeftPanel() {
      return (
        (this.isPinboardDualModeVisible ||
          (this.isPinboardSingleModeVisible &&
            this.activePinboardConfig.singlePanelSide === 'left')) &&
        this.isPinboardLayoutEnabled &&
        this.sidebarVisible
      );
    },
    showRightPanel() {
      return (
        (this.isPinboardDualModeVisible ||
          (this.isPinboardSingleModeVisible &&
            this.activePinboardConfig.singlePanelSide === 'right')) &&
        this.isPinboardLayoutEnabled &&
        this.sidebarVisible
      );
    },
    activePinboardConfig() {
      return { singlePanelSide: 'left' };
    },
    routeKey() {
      const route = this.$route;
      if (route.name !== 'market-details') return route.fullPath;

      const query = { ...route.query };
      delete query.contractId;
      const qs = new URLSearchParams(query).toString();
      const key = `market-details:${route.params.marketId}`;
      return qs ? `${key}?${qs}` : key;
    },
  },
};
